import { useState, useCallback, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for managing file attachments
 * @param {string} orderId - Order ID
 * @param {object} options - Hook options
 * @returns {object} Attachment state and methods
 */
export function useAttachments(orderId, options = {}) {
  const {
    kindFilter = null,
    autoRefresh = true,
    refreshInterval = 10000, // 10 seconds
  } = options;

  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);
  const isVisibleRef = useRef(true);

  // Get auth token
  const getToken = useCallback(() => {
    return localStorage.getItem('token');
  }, []);

  // Fetch files list
  const fetchFiles = useCallback(async () => {
    if (!orderId) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      let url = `${BACKEND_URL}/api/orders/${orderId}/files`;
      if (kindFilter) {
        url += `?kind=${kindFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have permission to view these files');
        }
        if (response.status === 404) {
          throw new Error('Order not found');
        }
        throw new Error(`Failed to fetch files: ${response.statusText}`);
      }

      const data = await response.json();
      setFiles(data);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [orderId, kindFilter, getToken]);

  // Upload file
  const uploadFile = useCallback(async (file, metadata = {}) => {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', metadata.kind || 'deliverable');
    if (metadata.messageId) {
      formData.append('message_id', metadata.messageId);
    }

    const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Upload failed: ${response.statusText}`);
    }

    const uploadedFile = await response.json();
    
    // Add to local state immediately
    setFiles(prev => [uploadedFile, ...prev]);
    
    return uploadedFile;
  }, [orderId, getToken]);

  // Delete file
  const deleteFile = useCallback(async (fileId) => {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${BACKEND_URL}/api/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Delete failed: ${response.statusText}`);
    }

    // Remove from local state
    setFiles(prev => prev.filter(f => f.id !== fileId));

    return true;
  }, [getToken]);

  // Get download URL with auth
  const getDownloadUrl = useCallback((fileId) => {
    const token = getToken();
    return {
      url: `${BACKEND_URL}/api/files/${fileId}/download`,
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    };
  }, [getToken]);

  // Get preview URL with auth
  const getPreviewUrl = useCallback((fileId) => {
    const token = getToken();
    return {
      url: `${BACKEND_URL}/api/files/${fileId}/preview`,
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    };
  }, [getToken]);

  // Download file as blob (for header auth)
  const downloadFileBlob = useCallback(async (fileId, fileName) => {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${BACKEND_URL}/api/files/${fileId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [getToken]);

  // Fetch preview as blob (for header auth)
  const fetchPreviewBlob = useCallback(async (fileId) => {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${BACKEND_URL}/api/files/${fileId}/preview`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Preview not available');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }, [getToken]);

  // Setup auto-refresh
  useEffect(() => {
    if (!autoRefresh || !orderId) return;

    // Initial fetch
    fetchFiles();

    // Handle visibility change
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      if (isVisibleRef.current) {
        fetchFiles();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Setup polling - only refresh if there are pending files
    const startPolling = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }

      refreshTimerRef.current = setInterval(() => {
        if (isVisibleRef.current) {
          // Check if any files are still processing
          setFiles(currentFiles => {
            const hasProcessing = currentFiles.some(f => f.status === 'pending');
            if (hasProcessing) {
              fetchFiles();
            }
            return currentFiles;
          });
        }
      }, refreshInterval);
    };

    startPolling();

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [orderId, autoRefresh, refreshInterval, fetchFiles]);

  // Check if any files are processing
  const hasProcessingFiles = files.some(f => f.status === 'pending');

  return {
    files,
    isLoading,
    error,
    hasProcessingFiles,
    refresh: fetchFiles,
    uploadFile,
    deleteFile,
    getDownloadUrl,
    getPreviewUrl,
    downloadFileBlob,
    fetchPreviewBlob,
  };
}
